import React from 'react';

// ESG Platform subscription component
function ESGPlatformSubscription({ onRequestInfo }) {
    const subscriptions = [
        {
            id: "basic",
            name: "Starter",
            price: "50",
            features: [
                "ESG data collection & centralization",
                "Carbon footprint calculation",
                "Basic ESG metrics dashboard",
                "Standard regulatory reporting",
                "Email support",
                "Up to 5 users"
            ]
        },
        {
            id: "professional",
            name: "Professional",
            price: "150",
            popular: true,
            features: [
                "All Starter features",
                "Advanced analytics & benchmarking",
                "Custom metrics & KPI tracking",
                "Automated data collection",
                "TCFD & SASB framework alignment",
                "Priority support",
                "Up to 15 users"
            ]
        },
        {
            id: "enterprise",
            name: "Enterprise",
            price: "500",
            features: [
                "All Professional features",
                "Custom dashboards & white labeling",
                "API integrations & data connectors",
                "Supply chain ESG assessment",
                "Advanced scenario analysis",
                "Dedicated account manager",
                "Unlimited users"
            ]
        }
    ];

    return (
        <div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {subscriptions.map((sub) => (
                    <div key={sub.id} className={`bg-gradient-to-b from-gray-800/90 to-gray-900/90 backdrop-blur-sm rounded-xl overflow-hidden transform transition-all duration-300 hover:scale-105 hover:shadow-xl ${sub.popular ? 'ring-2 ring-green-400 shadow-lg shadow-green-500/20' : 'border border-gray-700'}`}>
                        {sub.popular && (
                            <div className="bg-gradient-to-r from-green-600 to-green-400 text-white text-center py-2 font-medium">
                                Most Popular
                            </div>
                        )}
                        <div className="p-8">
                            <h3 className="text-2xl font-bold mb-2">{sub.name}</h3>
                            <div className="flex items-baseline mb-6">
                                <span className="text-3xl font-bold">£{sub.price}</span>
                                <span className="text-gray-400 ml-2">/month</span>
                            </div>
                            <ul className="space-y-3 mb-8">
                                {sub.features.map((feature, index) => (
                                    <li key={index} className="flex items-center">
                                        <i className="fas fa-check text-green-400 mr-3"></i>
                                        <span className="text-gray-300">{feature}</span>
                                    </li>
                                ))}
                            </ul>
                            <button 
                                onClick={() => onRequestInfo(`esg-${sub.id}`)}
                                className={`w-full py-3 rounded-lg ${
                                    sub.popular 
                                        ? 'bg-gradient-to-r from-green-600 to-green-400 hover:from-green-500 hover:to-green-300 text-white' 
                                        : 'bg-gray-700 hover:bg-gray-600 text-white'
                                } transition-all`}
                            >
                                {sub.popular ? 'Get Started Now' : 'Request Information'}
                            </button>
                        </div>
                    </div>
                ))}
            </div>
            
            {/* ESG platform benefits */}
            <div className="mt-12 bg-gradient-to-br from-gray-800/40 to-gray-900/40 p-8 rounded-xl border border-gray-700">
                <h3 className="text-2xl font-bold mb-6 text-center">Why Choose Our ESG Platform?</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex items-start">
                        <div className="bg-green-500/20 rounded-full w-12 h-12 flex items-center justify-center mr-4 flex-shrink-0">
                            <i className="fas fa-shield-check text-green-400 text-xl"></i>
                        </div>
                        <div>
                            <h4 className="font-bold text-lg mb-2">Regulatory Compliance</h4>
                            <p className="text-gray-300">Stay ahead of evolving ESG regulations with built-in compliance frameworks for TCFD, SASB, GRI, and more.</p>
                        </div>
                    </div>
                    <div className="flex items-start">
                        <div className="bg-green-500/20 rounded-full w-12 h-12 flex items-center justify-center mr-4 flex-shrink-0">
                            <i className="fas fa-tachometer-alt text-green-400 text-xl"></i>
                        </div>
                        <div>
                            <h4 className="font-bold text-lg mb-2">Performance Improvement</h4>
                            <p className="text-gray-300">Track your ESG performance over time, identify trends, and implement data-driven sustainability initiatives.</p>
                        </div>
                    </div>
                    <div className="flex items-start">
                        <div className="bg-green-500/20 rounded-full w-12 h-12 flex items-center justify-center mr-4 flex-shrink-0">
                            <i className="fas fa-file-alt text-green-400 text-xl"></i>
                        </div>
                        <div>
                            <h4 className="font-bold text-lg mb-2">Simplified Reporting</h4>
                            <p className="text-gray-300">Generate professional ESG reports with one click for investors, stakeholders, and regulatory submissions.</p>
                        </div>
                    </div>
                    <div className="flex items-start">
                        <div className="bg-green-500/20 rounded-full w-12 h-12 flex items-center justify-center mr-4 flex-shrink-0">
                            <i className="fas fa-users text-green-400 text-xl"></i>
                        </div>
                        <div>
                            <h4 className="font-bold text-lg mb-2">Stakeholder Engagement</h4>
                            <p className="text-gray-300">Impress investors and customers with transparent, data-backed sustainability reporting and communications.</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ESG Form Component
function ESGPlatformForm({ selectedTier }) {
    const [formData, setFormData] = React.useState({
        name: '',
        email: '',
        company: '',
        position: '',
        phone: '',
        companySize: '',
        interests: selectedTier || 'esg-platform',
        message: '',
        formType: 'esg_platform'
    });
    const [submitStatus, setSubmitStatus] = React.useState(null);
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    React.useEffect(() => {
        if (selectedTier) {
            setFormData(prev => ({
                ...prev,
                interests: selectedTier
            }));
        }
    }, [selectedTier]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        
        try {
            // Format data to match the partnership service expected structure
            const submissionData = {
                contactName: formData.name,
                companyName: formData.company,
                email: formData.email,
                phone: formData.phone,
                companySize: formData.companySize,
                interests: formData.interests,
                message: formData.message,
                formType: formData.formType,
                position: formData.position
            };

            // Use the partnership service for form submission
            if (window.partnershipService) {
                await window.partnershipService.createPartnershipInquiry(submissionData);
                setSubmitStatus('success');
                
                // Reset form after successful submission
                setFormData({
                    name: '',
                    email: '',
                    company: '',
                    position: '',
                    phone: '',
                    companySize: '',
                    interests: 'esg-platform',
                    message: '',
                    formType: 'esg_platform'
                });
            } else {
                throw new Error('Partnership service not available');
            }
        } catch (error) {
            console.error('Form submission error:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="bg-gray-800/80 backdrop-blur-sm rounded-xl p-8 border border-gray-700">
            <h2 className="text-2xl font-bold mb-6">Request ESG Platform Demo</h2>
            {submitStatus === 'success' ? (
                <div className="text-center py-8">
                    <div className="w-20 h-20 bg-gradient-to-br from-green-500/30 to-green-400/10 rounded-full flex items-center justify-center mx-auto mb-6">
                        <i className="fas fa-check text-green-400 text-3xl"></i>
                    </div>
                    <h3 className="text-2xl font-bold mb-4">Thank You!</h3>
                    <p className="text-gray-300 mb-8 text-lg">
                        We've received your request for an ESG platform demo. One of our specialists will contact you within 24 hours to schedule your personalized walkthrough.
                    </p>
                    <button 
                        onClick={() => setSubmitStatus(null)}
                        className="px-8 py-3 bg-gradient-to-r from-green-600 to-green-400 hover:from-green-500 hover:to-green-300 rounded-lg text-white transition-all transform hover:scale-105"
                    >
                        Request Another Demo
                    </button>
                </div>
            ) : submitStatus === 'error' ? (
                <div className="text-center py-8">
                    <div className="w-20 h-20 bg-gradient-to-br from-red-500/30 to-red-400/10 rounded-full flex items-center justify-center mx-auto mb-6">
                        <i className="fas fa-exclamation-triangle text-red-400 text-3xl"></i>
                    </div>
                    <h3 className="text-2xl font-bold mb-4">Error</h3>
                    <p className="text-gray-300 mb-8 text-lg">
                        There was an error submitting your ESG platform request. Please try again or contact us directly.
                    </p>
                    <button 
                        onClick={() => setSubmitStatus(null)}
                        className="px-8 py-3 bg-gradient-to-r from-purple-600 to-purple-400 hover:from-purple-500 hover:to-purple-300 rounded-lg text-white transition-all transform hover:scale-105"
                    >
                        Try Again
                    </button>
                </div>
            ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium mb-2 flex items-center">
                                <i className="fas fa-user text-blue-400 mr-2"></i> 
                                Full Name *
                            </label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-3 bg-gray-700/70 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                placeholder="Your name"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-2 flex items-center">
                                <i className="fas fa-envelope text-blue-400 mr-2"></i>
                                Business Email *
                            </label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-3 bg-gray-700/70 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                placeholder="your.email@company.com"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-2 flex items-center">
                                <i className="fas fa-building text-blue-400 mr-2"></i>
                                Company Name *
                            </label>
                            <input
                                type="text"
                                name="company"
                                value={formData.company}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-3 bg-gray-700/70 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                placeholder="Your company"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-2 flex items-center">
                                <i className="fas fa-briefcase text-blue-400 mr-2"></i>
                                Position/Title
                            </label>
                            <input
                                type="text"
                                name="position"
                                value={formData.position}
                                onChange={handleChange}
                                className="w-full px-4 py-3 bg-gray-700/70 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                placeholder="Your role"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-2 flex items-center">
                                <i className="fas fa-phone text-blue-400 mr-2"></i>
                                Phone Number
                            </label>
                            <input
                                type="tel"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                className="w-full px-4 py-3 bg-gray-700/70 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                placeholder="Your phone number"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-2 flex items-center">
                                <i className="fas fa-users text-blue-400 mr-2"></i>
                                Company Size
                            </label>
                            <select
                                name="companySize"
                                value={formData.companySize}
                                onChange={handleChange}
                                className="w-full px-4 py-3 bg-gray-700/70 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            >
                                <option value="">Select company size</option>
                                <option value="startup">Startup (1-10 employees)</option>
                                <option value="small">Small Business (11-50 employees)</option>
                                <option value="medium">Medium Business (51-200 employees)</option>
                                <option value="large">Large Enterprise (201-1000 employees)</option>
                                <option value="enterprise">Enterprise (1000+ employees)</option>
                            </select>
                        </div>
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium mb-2 flex items-center">
                            <i className="fas fa-comment text-blue-400 mr-2"></i>
                            Additional Information
                        </label>
                        <textarea
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            rows={4}
                            className="w-full px-4 py-3 bg-gray-700/70 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            placeholder="Tell us about your ESG reporting needs and goals..."
                        />
                    </div>
                    
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full py-4 bg-gradient-to-r from-green-600 to-green-400 hover:from-green-500 hover:to-green-300 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white font-medium text-lg transition-all transform hover:scale-105"
                    >
                        {isSubmitting ? (
                            <span className="flex items-center justify-center">
                                <i className="fas fa-spinner fa-spin mr-2"></i>
                                Submitting...
                            </span>
                        ) : (
                            <span className="flex items-center justify-center">
                                <i className="fas fa-paper-plane mr-2"></i>
                                Schedule Demo
                            </span>
                        )}
                    </button>
                </form>
            )}
        </div>
    );
}

// Main ESG Platform Page Component
function ESGPlatformPage() {
    const [selectedOffering, setSelectedOffering] = React.useState(null);

    const handleOfferingSelect = (offeringType) => {
        setSelectedOffering(offeringType);
        // Scroll to form
        const formElement = document.getElementById('esg-form');
        if (formElement) {
            formElement.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 text-white">
            {/* Hero Section */}
            <section className="relative py-20 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-green-500/10 to-blue-500/10"></div>
                <div className="container mx-auto px-6 relative z-10">
                    <div className="text-center max-w-4xl mx-auto">
                        <h1 className="text-5xl md:text-7xl font-bold mb-8 bg-gradient-to-r from-green-400 to-blue-400 bg-clip-text text-transparent">
                            ESG Platform
                        </h1>
                        <p className="text-xl md:text-2xl text-gray-300 mb-12 leading-relaxed">
                            Transform your ESG reporting with our comprehensive data management and compliance platform. 
                            Streamline sustainability metrics, meet regulatory requirements, and impress investors with 
                            transparent, data-driven ESG reporting.
                        </p>
                        <div className="flex flex-col sm:flex-row justify-center gap-6">
                            <a 
                                href="#esg-platform-section" 
                                className="px-8 py-4 bg-gradient-to-r from-green-600 to-green-400 hover:from-green-500 hover:to-green-300 rounded-lg text-white font-bold text-lg transition-all transform hover:scale-105 flex items-center justify-center"
                            >
                                <i className="fas fa-chart-line mr-3"></i>
                                Explore Pricing Plans
                            </a>
                            <a 
                                href="#esg-form" 
                                className="px-8 py-4 bg-white text-gray-900 hover:bg-gray-100 rounded-lg font-bold text-lg transition-all transform hover:scale-105 flex items-center justify-center"
                            >
                                <i className="fas fa-calendar-check mr-3"></i>
                                Schedule Demo
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Main Content */}
            <section className="py-16">
                <div className="container mx-auto px-6">
                    {/* ESG Platform Section */}
                    <div id="esg-platform-section" className="mb-24 py-8">
                        <div className="flex items-center justify-center mb-8">
                            <div className="w-20 h-20 bg-green-500/30 rounded-full flex items-center justify-center mr-4">
                                <i className="fas fa-chart-line text-green-400 text-3xl"></i>
                            </div>
                            <h2 className="text-4xl font-bold">ESG Platform <span className="text-green-400">Subscription</span></h2>
                        </div>
                        <p className="text-center text-gray-300 mb-12 max-w-3xl mx-auto text-lg">
                            Choose the perfect ESG data management solution for your organization. From basic metrics tracking 
                            to enterprise-grade compliance reporting, we have the tools to meet your sustainability goals.
                        </p>
                        <ESGPlatformSubscription onRequestInfo={handleOfferingSelect} />
                    </div>

                    {/* Form and Additional Info */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-16">
                        <div id="esg-form">
                            <ESGPlatformForm selectedTier={selectedOffering} />
                        </div>
                        <div className="space-y-8">
                            <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-lg rounded-2xl border border-gray-700 shadow-xl overflow-hidden">
                                <div className="bg-purple-600/20 border-b border-purple-600/30 p-4 text-center">
                                    <h2 className="text-2xl font-bold text-white">Platform Benefits</h2>
                                </div>
                                <div className="p-8">
                                    <div className="space-y-6">
                                        <div className="flex items-start">
                                            <div className="bg-green-500/20 w-12 h-12 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                                                <i className="fas fa-certificate text-green-400 text-xl"></i>
                                            </div>
                                            <div>
                                                <h3 className="font-bold text-lg mb-2">Sustainability Intelligence</h3>
                                                <p className="text-gray-300">Turn your environmental data into actionable insights with our advanced ESG analytics platform.</p>
                                            </div>
                                        </div>
                                        <div className="flex items-start">
                                            <div className="bg-green-500/20 w-12 h-12 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                                                <i className="fas fa-chart-bar text-green-400 text-xl"></i>
                                            </div>
                                            <div>
                                                <h3 className="font-bold text-lg mb-2">TCFD & SASB Compliance</h3>
                                                <p className="text-gray-300">Meet global ESG reporting standards with built-in frameworks and compliance tools.</p>
                                            </div>
                                        </div>
                                        <div className="flex items-start">
                                            <div className="bg-green-500/20 w-12 h-12 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                                                <i className="fas fa-handshake text-green-400 text-xl"></i>
                                            </div>
                                            <div>
                                                <h3 className="font-bold text-lg mb-2">Investor-Grade Reporting</h3>
                                                <p className="text-gray-300">Generate professional ESG reports that satisfy investor demands for transparent sustainability data.</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ESG Platform FAQ Section */}
                    <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-lg rounded-2xl border border-gray-700 shadow-xl p-10 mb-16">
                        <h2 className="text-3xl font-bold mb-8 text-center">ESG Platform <span className="text-green-400">FAQ</span></h2>
                        <p className="text-center text-gray-300 mb-12 max-w-3xl mx-auto">
                            Common questions about our enterprise ESG data management and reporting solution
                        </p>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="bg-gray-800/70 rounded-xl p-6 border border-gray-700 hover:border-green-500/30 transition-all">
                                <h3 className="text-xl font-bold mb-4 flex items-center">
                                    <i className="fas fa-question-circle text-green-400 mr-3"></i>
                                    What ESG reporting frameworks does your platform support?
                                </h3>
                                <p className="text-gray-300">
                                    Our ESG platform supports all major reporting frameworks including TCFD, SASB, GRI, CDP, and EU SFDR. The system automatically maps your data to the appropriate disclosure requirements for each framework, eliminating duplicate data entry.
                                </p>
                            </div>
                            
                            <div className="bg-gray-800/70 rounded-xl p-6 border border-gray-700 hover:border-green-500/30 transition-all">
                                <h3 className="text-xl font-bold mb-4 flex items-center">
                                    <i className="fas fa-question-circle text-green-400 mr-3"></i>
                                    How does the data collection process work?
                                </h3>
                                <p className="text-gray-300">
                                    Our platform offers multiple data collection methods: automated API integrations with existing systems, direct data uploads via CSV/Excel, manual data entry through customizable forms, and a mobile app for field data collection. We can connect to utility providers, ERP systems, and IoT devices.
                                </p>
                            </div>
                            
                            <div className="bg-gray-800/70 rounded-xl p-6 border border-gray-700 hover:border-green-500/30 transition-all">
                                <h3 className="text-xl font-bold mb-4 flex items-center">
                                    <i className="fas fa-question-circle text-green-400 mr-3"></i>
                                    Can the ESG platform handle Scope 3 emissions tracking?
                                </h3>
                                <p className="text-gray-300">
                                    Yes, our platform has comprehensive capabilities for Scope 3 emissions tracking. We provide supplier assessment tools, spend-based calculation methods, and supply chain mapping features to help you collect and analyze your value chain emissions data in compliance with GHG Protocol standards.
                                </p>
                            </div>
                            
                            <div className="bg-gray-800/70 rounded-xl p-6 border border-gray-700 hover:border-green-500/30 transition-all">
                                <h3 className="text-xl font-bold mb-4 flex items-center">
                                    <i className="fas fa-question-circle text-green-400 mr-3"></i>
                                    How customizable are the ESG dashboards and reports?
                                </h3>
                                <p className="text-gray-300">
                                    Our dashboards are highly customizable. You can create role-specific views (executive, sustainability team, operations), custom metrics and KPIs, personalized report templates, and branded outputs. The Enterprise tier includes white-labeling options for investor-facing reports.
                                </p>
                            </div>
                            
                            <div className="bg-gray-800/70 rounded-xl p-6 border border-gray-700 hover:border-green-500/30 transition-all">
                                <h3 className="text-xl font-bold mb-4 flex items-center">
                                    <i className="fas fa-question-circle text-green-400 mr-3"></i>
                                    What security measures protect our ESG data?
                                </h3>
                                <p className="text-gray-300">
                                    Our platform employs enterprise-grade security including SOC 2 compliance, end-to-end encryption, role-based access controls, multi-factor authentication, regular security audits, and GDPR compliance. All data is backed up in multiple secure locations.
                                </p>
                            </div>
                            
                            <div className="bg-gray-800/70 rounded-xl p-6 border border-gray-700 hover:border-green-500/30 transition-all">
                                <h3 className="text-xl font-bold mb-4 flex items-center">
                                    <i className="fas fa-question-circle text-green-400 mr-3"></i>
                                    How long does implementation typically take?
                                </h3>
                                <p className="text-gray-300">
                                    Basic implementation can be completed in as little as 2-4 weeks. More complex enterprise integrations typically take 6-8 weeks. Our implementation team will create a custom onboarding plan based on your specific requirements and existing systems.
                                </p>
                            </div>
                        </div>
                    </div>
                    
                    {/* Final CTA Section */}
                    <div className="bg-gradient-to-br from-green-900/20 to-blue-900/20 backdrop-blur-lg rounded-2xl border border-green-500/30 shadow-xl p-10 mb-8">
                        <div className="text-center max-w-3xl mx-auto">
                            <h2 className="text-3xl font-bold mb-6">Transform Your ESG Reporting Today</h2>
                            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                                Join hundreds of organizations that have revolutionized their sustainability reporting with our enterprise ESG platform. Schedule a demo to see how our solution can help you meet regulatory requirements, impress investors, and drive your sustainability initiatives forward.
                            </p>
                            <div className="flex flex-wrap justify-center gap-6">
                                <a 
                                    href="#esg-platform-section" 
                                    className="px-8 py-4 bg-gradient-to-r from-green-600 to-green-400 hover:from-green-500 hover:to-green-300 rounded-lg text-white font-bold text-lg transition-all transform hover:scale-105 flex items-center"
                                >
                                    <i className="fas fa-chart-line mr-3"></i>
                                    Explore ESG Platform
                                </a>
                                <a 
                                    href="#esg-form" 
                                    className="px-8 py-4 bg-white text-gray-900 hover:bg-gray-100 rounded-lg font-bold text-lg transition-all transform hover:scale-105 flex items-center"
                                >
                                    <i className="fas fa-calendar-check mr-3"></i>
                                    Schedule Your Demo
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

window.ESGPlatformPage = ESGPlatformPage;
export default ESGPlatformPage;
